import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import { getIdData, addData, updateData } from '../../lib/studentnotes';
import { TextEditor } from '../Common/TextEditor';
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents';

const NoteModal = ({ isOpen, toggle, instructorId, studentId, editingNote, onSave, isDarkMode }) => {
    const [title, setTitle] = useState('');
    const [noteData, setNoteData] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [showToast, setShowToast] = useState(false);

    const showToastMessage = (message) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    }

    const isEdit = !!editingNote;

    useEffect(() => {
        if (isOpen) {
            if (isEdit) {
                fetchNoteData();
            } else {
                setTitle('');
                setNoteData('');
            }
            setError('');
        }
    }, [isOpen, editingNote]);

    const fetchNoteData = async () => {
        try {
            setLoading(true);
            const data = await getIdData(editingNote.id);
            if (data && data.length > 0) {
                setTitle(data[0].title || '');
                setNoteData(data[0].description || '');
            }
        } catch (error) {
            console.error('Error fetching note data:', error);
            setError('Failed to load note data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (isEdit) {
                await updateData({
                    id: editingNote.id,
                    title: title,
                    description: noteData
                });
            } else {
                await addData({
                    title: title,
                    description: noteData,
                    instructorId: instructorId,
                    studentId: studentId
                });
            }

            showToastMessage(isEdit ? 'Note updated successfully!' : 'Note added successfully!');
            if (onSave) await onSave();
            setTimeout(() => toggle(), 2000);
        } catch (error) {
            console.error('Error saving note:', error);
            setError('Failed to save note');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isDarkMode && isOpen) {
            const style = document.createElement('style');
            style.id = 'note-modal-ckeditor-dark';
            style.textContent = `
                .modal-content .ck-editor__editable,
                .modal-content .ck-content,
                .modal-content .ck-content p,
                .modal-content .ck-content span,
                .modal-content .ck-content li,
                .modal-content .ck-content h1,
                .modal-content .ck-content h2,
                .modal-content .ck-content h3 {
                    color: #fff !important;
                }
                .modal-content .ck-editor__editable {
                    background-color: #2a2d35 !important;
                }
            `;
            document.head.appendChild(style);
            return () => {
                const existingStyle = document.getElementById('note-modal-ckeditor-dark');
                if (existingStyle) existingStyle.remove();
            };
        }
    }, [isDarkMode, isOpen]);

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className={isDarkMode ? 'dark-modal' : ''}>
            <ModalHeader toggle={toggle}>
                {isEdit ? 'Update Note' : 'Add Note'}
            </ModalHeader>
            <ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}
                <form onSubmit={handleSubmit} id="note-modal-form">
                    <Row>
                        <Col lg="12">
                            <FormGroup>
                                <Label className="form-control-label">Title</Label>
                                <Input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Enter Title"
                                    required
                                    disabled={loading}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg="12">
                            <FormGroup>
                                <Label className="form-control-label">Description</Label>
                                <TextEditor value={noteData} setValue={setNoteData} fixedViewportHeight={320} />
                            </FormGroup>
                        </Col>
                    </Row>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" type="submit" form="note-modal-form" disabled={loading}>
                    {loading ? 'Saving...' : isEdit ? 'Update Note' : 'Add Note'}
                </Button>
                <Button color="secondary" onClick={toggle} disabled={loading}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default NoteModal;
